import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertBanner } from "src/components/AlertBanner";
import { Logo } from "src/components/Logo";
import { useToast } from "src/components/Toast";
import { useAuth } from "src/context";
import { usePageView } from "src/hooks/usePageView";
import { validEmail } from "src/utils";
import { EmailForm } from "./EmailForm";
import { Footer } from "./Footer";

interface LoginLocationState {
  errorCode?: string;
}

export default () => {
  const [inputValue, setInputValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [loading, setLoading] = useState(false);
  const authObject = useAuth();
  const location = useLocation();
  const state = location.state as LoginLocationState | null;
  const { setPendingToast } = useToast();
  usePageView("Login");

  useEffect(() => {
    if (state?.errorCode && state.errorCode === "auth/invalid-action-code") {
      setPendingToast(
        "The magic link has expired. Please sign in again.",
        "error",
      );
    }
  }, [state]);

  const signIn = () => {
    let invalid = false;
    const trimmedEmail = inputValue.trim();
    if (!validEmail(trimmedEmail)) {
      setShowEmailError(true);
      invalid = true;
    }
    if (passwordValue.trim() === "") {
      setShowPasswordError(true);
      invalid = true;
    }
    if (invalid) {
      return;
    }

    setLoading(true);
    const pwd = passwordValue;
    authObject
      .signInWithEmail(trimmedEmail, pwd)
      .then(() => {
        window.location.href = `/select-location`;
      })
      .catch((error) => {
        console.info("Sign in ERROR :: ", error, trimmedEmail);
        setShowEmailError(true);
        setShowPasswordError(true);
        setLoading(false);
        setPendingToast("Unable to sign in. Check your credentials.", "error");
      });
  };
  const titleText = "Welcome Back";
  const subTitleText = "To get started, please sign in.";
  return (
    <div className="flex flex-col justify-between items-center min-h-screen">
      <div className="flex flex-col items-center w-full">
        <div className="mt-8 mb-22">
          <Logo />
        </div>
        <div className="relative w-full max-w-[410px] mx-auto">
          <AlertBanner
            title="Updated login process"
            descriptionHTML={`If you haven't signed in since the update, use the <span style="font-weight:bold;">Forgot Password</span> link to set a new password.`}
            footer="Updated: 12/5/2025"
          />
        </div>
        <div className="flex relative flex-col items-center mx-auto text-center mt-18">
          <h1 className="text-[28px] leading-[0.75em] font-semibold mb-4 text-on-surface-primary">
            {titleText}
          </h1>
          <p className="text-[13px] font-normal leading-[0.75em] mb-10 max-w-[600px] text-on-surface-muted">
            {subTitleText}
          </p>
          <EmailForm
            emailValue={inputValue}
            passwordValue={passwordValue}
            showEmailError={showEmailError}
            showPasswordError={showPasswordError}
            showPassword={showPassword}
            onEmailChange={(evt) => setInputValue(evt.target.value)}
            onEmailFocus={() => setShowEmailError(false)}
            onEmailBlur={(evt) => {
              const trimmedValue = evt.target.value.trim();
              setInputValue(trimmedValue);
              setShowEmailError(!validEmail(trimmedValue));
            }}
            onPasswordChange={(evt) => setPasswordValue(evt.target.value)}
            onPasswordFocus={() => setShowPasswordError(false)}
            onPasswordBlur={(evt) => {
              const trimmedPwd = evt.target.value.trim();
              setPasswordValue(trimmedPwd);
              setShowPasswordError(trimmedPwd.length === 0);
            }}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            onSubmit={() => !showEmailError && !showPasswordError && signIn()}
            loading={loading}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};
